import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { UserSedoristService } from 'src/app/services/user-sedorist.service';
import { UserPricetarService } from 'src/app/services/user-pricetar.service';
import { Condition, UserPricetarProductInput, UserSedoristProductInput } from 'src/gql/generated';
import { DateService } from 'src/app/services/date.service';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { UserService } from 'src/app/services/user.service';

import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { Textarea } from 'primeng/textarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { JapanDatePipe } from 'src/app/pipe/japan-date.pipe';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    RouterModule,
    ButtonModule,
    TabsModule,
    Textarea,
    RadioButtonModule,
    TableModule,
    JapanDatePipe,
  ],
})
export class RegisterComponent implements OnInit {
  pricetar: string;
  sedorist: string;

  isSedoristSearchList = 'false';

  pricetars: Array<Priceter>;
  sedorists: Array<Sedorist>;

  pricetarSkipped = 0; // 保留中(金額未確定)として除外した件数

  constructor(
    private router: Router,
    private message: MessageService,
    private routingService: RoutingService,
    private userService: UserService,
    private dateService: DateService,
    private sedoristService: UserSedoristService,
    private pricetarService: UserPricetarService,
  ) {
    this.routingService.loginRequired();
    this.userService.shareRequired();
  }

  ngOnInit(): void {}

  goBack() {
    this.router.navigate(['/share']);
  }

  doPricetarCheck() {
    if (!this.pricetar) {
      return;
    }
    this.sedorists = [];
    this.pricetars = this.buildPricetars(this.pricetar.split('\n').map((row) => row.split('\t')));
  }

  private buildPricetars(rows: Array<Array<string>>): Array<Priceter> {
    const converted = rows
      .map((data) => {
        if (data[0] === '注文日') {
          // ヘッダー行は後段の filter で落とす
          return undefined;
        }
        return { pending: data[13] === 'Pending', product: this.convertPricetar(data) };
      })
      .filter((x) => x !== undefined && x.product.asin !== undefined);
    // 保留中(出荷状態=Pending)の注文は売上金額が未確定のため除外する
    // 出荷状態が取れない場合に備えて金額でも判定する
    const confirmed = converted.filter(
      (x) => !x.pending && Number.isFinite(x.product.sellingPrice) && x.product.sellingPrice > 0,
    );
    this.pricetarSkipped = converted.length - confirmed.length;
    return confirmed.map((x) => x.product);
  }

  doSedoristCheck() {
    if (!this.sedorist) {
      return;
    }
    this.pricetars = [];
    this.sedorists = this.buildSedorists(this.sedorist.split('\n').map((row) => row.split('\t')));
  }

  private buildSedorists(rows: Array<Array<string>>): Array<Sedorist> {
    return rows
      .map((data) => {
        if (data[0] === 'SKU') {
          // ヘッダー行は後段の filter で落とす
          return undefined;
        }
        const p = this.convertSedorist(data);
        return p;
      })
      .filter((p) => p !== undefined && p.asin !== undefined);
  }

  async onCsvSelected(event: Event, target: 'pricetar' | 'sedorist') {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const text = this.decodeCsv(await file.arrayBuffer());
    const rows = this.parseCsv(text);
    if (target === 'pricetar') {
      this.sedorists = [];
      this.pricetars = this.buildPricetars(rows);
    } else {
      this.pricetars = [];
      this.sedorists = this.buildSedorists(rows);
    }
    input.value = '';
  }

  // 文字コード自動判別: プライスターはUTF-8、せどりすとはShift_JISのCSVを出力する
  private decodeCsv(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
      return new TextDecoder('utf-8').decode(bytes.subarray(3));
    }
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return new TextDecoder('shift_jis').decode(bytes);
    }
  }

  // 商品名内のカンマ・改行・引用符("")に対応したCSVパース
  private parseCsv(text: string): Array<Array<string>> {
    const rows: Array<Array<string>> = [];
    let row: Array<string> = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field);
        field = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') {
          i++;
        }
        row.push(field);
        field = '';
        rows.push(row);
        row = [];
      } else {
        field += c;
      }
    }
    if (field !== '' || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  doUploadPriceter() {
    if (!this.pricetars || this.pricetars.length < 1) {
      return;
    }
    const inputs: Array<UserPricetarProductInput> = [];
    this.pricetars.forEach((pricetar) => {
      let condition = Condition.New;
      if (pricetar.condition === 'old') {
        condition = Condition.Old;
      }
      inputs.push({
        asin: pricetar.asin,
        sku: pricetar.sku,
        productName: pricetar.name,
        buyingPrice: pricetar.buyingPrice,
        sellingPrice: pricetar.sellingPrice,
        salesCommission: pricetar.costPrice,
        condition,
        orderDate: this.dateService.toStringDate(pricetar.orderDate),
        deliveryRoute: pricetar.deliveryRoute,
        isPublic: true,
      });
    });
    this.pricetarService.register({ userPricetarInputs: inputs }, (resp) => {
      this.message.setFlashMessageWithClear('success', '成功', 'プライスター情報の登録に成功しました');
    });
    this.pricetar = '';
    this.pricetars = [];
  }

  doUploadSedorist() {
    if (!this.sedorists || this.sedorists.length < 1) {
      return;
    }
    const inputs: Array<UserSedoristProductInput> = [];
    this.sedorists.forEach((sedorist) => {
      let condition = Condition.New;
      if (sedorist.condition === 'old') {
        condition = Condition.Old;
      }
      const jan = sedorist.jan.replace(/\r?\n? /g, '');
      inputs.push({
        asin: sedorist.asin,
        jan,
        sku: sedorist.sku,
        productName: sedorist.name,
        buyingPrice: sedorist.buyingPrice,
        sellingPrice: sedorist.sellingPrice,
        condition,
        deliveryRoute: sedorist.deliveryRoute,
        buyingDate: this.dateService.toStringDate(sedorist.buyingDate),
        isPublic: true,
      });
    });
    this.sedoristService.register({ userSedoristInputs: inputs }, (resp) => {
      this.message.setFlashMessageWithClear('success', '成功', 'セドリスト情報の登録に成功しました');
    });
    this.sedorist = '';
    this.sedorists = [];
  }

  hasPriceter() {
    return this.pricetars && this.pricetars.length > 0;
  }

  hasSedorist() {
    return this.sedorists && this.sedorists.length > 0;
  }

  private convertPricetar(row: Array<string>): Priceter {
    const soldNum = Number(row[16]);
    return {
      asin: row[3],
      sku: row[4],
      name: row[1],
      buyingPrice: Math.ceil(Number(row[9]) / soldNum),
      sellingPrice: Math.ceil(Number(row[6]) / soldNum),
      costPrice: Math.ceil(Number(row[11]) / soldNum),
      condition: row[15],
      deliveryRoute: row[5],
      orderDate: new Date(row[0]),
    };
  }

  private convertSedorist(row: Array<string>): Sedorist {
    let jan = '';
    if (row[1] && row[1].match('[0-9]{13}')) {
      jan = row[1];
    }

    const asin = row[2];

    if (this.isSedoristSearchList === 'true') {
      return this.convertSedoristSearch(row, asin, jan);
    }

    const buyingDate = this.toValidDate(row[20]);
    return {
      asin,
      jan,
      sku: this.buildSedoristSku(row[0], asin, buyingDate),
      name: row[3],
      referencePrice: Number(row[9]),
      sellingPrice: Number(row[16]),
      buyingPrice: Number(row[14]),
      deliveryRoute: row[18],
      condition: row[17],
      buyingDate,
    };
  }

  private convertSedoristSearch(row: Array<string>, asin: string, jan: string): Sedorist {
    const referencePrice = row[9] === '-' ? undefined : Number(row[9]);
    const buyingDate = this.toValidDate(row[8]);
    return {
      asin,
      jan,
      sku: this.buildSedoristSku(row[0], asin, buyingDate),
      name: row[3],
      referencePrice,
      sellingPrice: Number(row[4]),
      buyingPrice: Number(row[14]),
      deliveryRoute: row[7],
      condition: row[22],
      buyingDate,
    };
  }

  // SKU 列が空のとき ASIN をそのまま SKU にすると、同じ商品を別の日に仕入れた行が
  // 同じ SKU を共有し、サーバ側の SKU 突合で 1 行に統合されてしまう。
  // 仕入日を付けて「1 仕入 = 1 SKU」にする。
  //
  // 日付部分は YYYYMMDD で固定する。DB の user_sedorist_products.buying_date は
  // date 型で時刻を持たないため、時刻まで含めるとサーバ側の既存データと突合できない。
  private buildSedoristSku(rawSku: string, asin: string, buyingDate: Date): string {
    if (rawSku) {
      return rawSku;
    }
    return `${asin}-${this.dateService.formatDate(buyingDate, 'YYYYMMDD')}`;
  }

  // 仕入日が読めない行は取込日で補う。SKU の日付部分にも使うため、
  // 検索リスト形式・通常形式のどちらも同じ扱いにする。
  private toValidDate(value: string): Date {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }
}

export interface Priceter {
  asin: string;
  sku: string;
  name: string;
  sellingPrice: number;
  buyingPrice: number;
  costPrice: number;
  deliveryRoute: string;
  condition: string;
  orderDate: Date;
}

export interface Sedorist {
  asin: string;
  jan: string;
  sku: string;
  name: string;
  referencePrice: number;
  sellingPrice: number;
  buyingPrice: number;
  deliveryRoute: string;
  condition: string;
  buyingDate: Date;
}
